import type { ReactNode } from 'react'
import { IntlProvider as ReactIntlProvider, type IntlConfig } from 'react-intl'

import EN_LOCALE from '@locales/en-US'
import DE_LOCALE from '@locales/de-DE'

import { DEFAULT_LOCALE, SHORT_LOCALES, shortToFull } from '../../locales.constants'
import { useLocale } from '../../LocaleContext'

const messagesFor = (short: SHORT_LOCALES) => (short === SHORT_LOCALES.de ? DE_LOCALE : EN_LOCALE)

// react-intl logs every missing key / bad ICU formatter to console.error by
// default. Customise so:
//   - tests fail loudly (catches missing-key drift that parity scripts can't —
//     they only verify EN↔DE symmetry, not that the key is actually used);
//   - dev still gets warnings;
//   - prod stays silent (no console spam at end users).
const handleIntlError: IntlConfig['onError'] = (err) => {
  if (process.env.NODE_ENV === 'test') {
    throw err
  }
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('[i18n]', err.code, err.message)
  }
}

interface Props {
  children: ReactNode
}

const IntlProvider = ({ children }: Props) => {
  const { locale } = useLocale()
  return (
    <ReactIntlProvider
      locale={shortToFull(locale)}
      defaultLocale={DEFAULT_LOCALE}
      messages={messagesFor(locale)}
      onError={handleIntlError}
    >
      {children}
    </ReactIntlProvider>
  )
}

export default IntlProvider
